/**
 * OrdersFilterBar
 *
 * Full filter bar component with URL persistence for the redesigned Orders page.
 * Features popover-based filters for queue status, colors, sizes, product types, and tags.
 * Admin users also see a location dropdown.
 *
 * Features:
 * - Animated filter badges with removal transitions
 * - Queue Status filter (Ready to Batch / All Orders)
 * - Sort order toggle (Oldest First / Newest First)
 * - Admin-only location filter
 * - Active filter badges with "Clear all" button
 *
 * Phase 12.6: Removed 'oos' from queue status options.
 * OOS orders are now managed via Production Issues screen.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Palette, Ruler, Shirt, Tag, X, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PopoverFilter, sanitizeFilterValue } from './PopoverFilter';
import { ANIMATIONS, getAnimationProps } from '@/lib/animations';
import { cn } from '@/lib/utils';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Queue status filter options
 * Phase 12.6: 'oos' removed - OOS orders managed via Production Issues screen
 */
export type QueueStatusFilter = 'ready' | 'all';
export type OrderTypeFilter = 'all' | 'single' | 'multi';
export type AgeRangeFilter = 'all' | 'today' | '1-3days' | '3+days';
/** pod_only is legacy (kept for persisted URLs); the menu offers the
 *  print-tech split pod_dtg / pod_dtf instead. */
export type FulfillmentTypeFilter = 'all' | 'pod_only' | 'pap_only' | 'mixed' | 'pod_dtg' | 'pod_dtf';
export type SortOrderFilter = 'oldest' | 'newest';

export interface OrdersPageFilters {
  queueStatus: QueueStatusFilter;
  orderType: OrderTypeFilter;
  ageRange: AgeRangeFilter;
  fulfillmentType: FulfillmentTypeFilter;
  sortOrder: SortOrderFilter;
  searchQuery: string;
  colors: string[];
  colorMatchMode: 'any' | 'only';
  sizes: string[];
  sizeMatchMode: 'any' | 'only';
  productTypes: string[];
  tags: string[];
}

// Default filter state - CRITICAL: only 'ready' by default (OOS hidden)
export const DEFAULT_FILTERS: OrdersPageFilters = {
  queueStatus: 'ready',
  orderType: 'all',
  ageRange: 'all',
  fulfillmentType: 'all',
  /**
   * BLU-787 — oldest first, matching what batching actually does.
   *
   * This defaulted to 'newest' while `resolveBatchScopes` always resolves and
   * trims oldest-first, so the screen and the batch disagreed by default and the
   * menu needed a line of copy to explain it away. Brett, 2026-08-13, on being
   * shown that line: "I actually thought it was always oldest first." When the
   * person who owns the product has the wrong model of the default, the default
   * is the bug.
   *
   * Oldest-first is also the right production default on its own merits: the
   * longest-waiting orders are the ones closest to breaching a ship date, so
   * they are what a floor should scan first. Purely presentational — nothing
   * about what gets batched changes, and the sort is still switchable.
   */
  sortOrder: 'oldest',
  searchQuery: '',
  colors: [],
  colorMatchMode: 'any',
  sizes: [],
  sizeMatchMode: 'any',
  productTypes: [],
  tags: [],
};

interface OrdersFilterBarProps {
  filters: OrdersPageFilters;
  onChange: (filters: OrdersPageFilters) => void;
  availableColors: string[];
  availableSizes: string[];
  availableProductTypes: string[];
  availableTags: string[];
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Every option label is a `production` key, read through t() at render so a
// zh session reads zh (the labels were hard-coded English until BLU-1506).

// Queue status options for dropdown
// Phase 12.6: Removed 'oos' - OOS orders managed via Production Issues screen
const QUEUE_STATUS_OPTIONS: { value: QueueStatusFilter; labelKey: string }[] = [
  { value: 'ready', labelKey: 'orders.filter.readyToBatch' },
  { value: 'all', labelKey: 'orders.filter.allOrders' },
];

// Order type options for segmented control
const ORDER_TYPE_OPTIONS: { value: OrderTypeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'orders.filter.all' },
  { value: 'single', labelKey: 'orders.filter.single' },
  { value: 'multi', labelKey: 'orders.filter.multi' },
];

// Age range options for dropdown
const AGE_RANGE_OPTIONS: { value: AgeRangeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'orders.filter.anyAge' },
  { value: 'today', labelKey: 'orders.filter.today' },
  { value: '1-3days', labelKey: 'orders.filter.1to3days' },
  { value: '3+days', labelKey: 'orders.filter.3plusDays' },
];

// Fulfillment type options for dropdown. POD splits by print technology
// (DTG/DTF) — legacy pod_only still parses from persisted URLs but is no
// longer offered.
const FULFILLMENT_TYPE_OPTIONS: { value: FulfillmentTypeFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'orders.filter.allTypes' },
  { value: 'pap_only', labelKey: 'orders.filter.papOnly' },
  { value: 'pod_dtg', labelKey: 'orders.filter.podDtg' },
  { value: 'pod_dtf', labelKey: 'orders.filter.podDtf' },
  { value: 'mixed', labelKey: 'orders.filter.mixed' },
];

// Sort order options for dropdown
const SORT_ORDER_OPTIONS: { value: SortOrderFilter; labelKey: string }[] = [
  { value: 'oldest', labelKey: 'orders.filter.oldest' },
  { value: 'newest', labelKey: 'orders.filter.newest' },
];

// ============================================================================
// COMPONENT
// ============================================================================

export function OrdersFilterBar({
  filters,
  onChange,
  availableColors,
  availableSizes,
  availableProductTypes,
  availableTags,
}: OrdersFilterBarProps) {
  const { t } = useTranslation('production');
  // Respect user's reduced motion preference
  const shouldReduceMotion = useReducedMotion();
  const badgeAnimProps = getAnimationProps(ANIMATIONS.badge, shouldReduceMotion);
  const collapseAnimProps = getAnimationProps(ANIMATIONS.collapse, shouldReduceMotion);

  // Update a specific filter
  const updateFilter = useCallback(<K extends keyof OrdersPageFilters>(
    key: K,
    value: OrdersPageFilters[K]
  ) => {
    onChange({ ...filters, [key]: value });
  }, [filters, onChange]);

  // Remove a single filter value (for array filters only)
  const removeFilter = useCallback((key: 'colors' | 'sizes' | 'productTypes' | 'tags', value: string) => {
    const current = filters[key];
    const newValue = current.filter(v => v !== value);
    const updates: Partial<OrdersPageFilters> = { [key]: newValue };
    // Auto-reset match mode when all values removed
    if (key === 'colors' && newValue.length === 0) updates.colorMatchMode = 'any';
    if (key === 'sizes' && newValue.length === 0) updates.sizeMatchMode = 'any';
    onChange({ ...filters, ...updates });
  }, [filters, onChange]);

  // Clear all filters (reset to defaults)
  const clearAllFilters = useCallback(() => {
    onChange(DEFAULT_FILTERS);
  }, [onChange]);

  // Count active filters (array filters only - dropdowns don't need badges)
  const activeFilterCount =
    filters.colors.length +
    filters.sizes.length +
    filters.productTypes.length +
    filters.tags.length;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {/* Single row: All filters inline, scrollable on overflow */}
        <div className="flex items-end gap-3 md:gap-4 flex-nowrap overflow-x-auto">
          {/* Queue Status dropdown with label */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('orders.filter.orderStatus')}</span>
            <Select
              value={filters.queueStatus}
              onValueChange={(value: QueueStatusFilter) => updateFilter('queueStatus', value)}
            >
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {QUEUE_STATUS_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Age Range dropdown with label */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('orders.filter.ageRange')}</span>
            <Select
              value={filters.ageRange}
              onValueChange={(value: AgeRangeFilter) => updateFilter('ageRange', value)}
            >
              <SelectTrigger className="w-full sm:w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGE_RANGE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Order Type dropdown with label (converted from toggle) */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('orders.filter.lineItems')}</span>
            <Select
              value={filters.orderType}
              onValueChange={(value: OrderTypeFilter) => updateFilter('orderType', value)}
            >
              <SelectTrigger className="w-full sm:w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Fulfillment Type dropdown with label */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('orders.filter.fulfillment')}</span>
            <Select
              value={filters.fulfillmentType}
              onValueChange={(value: FulfillmentTypeFilter) => updateFilter('fulfillmentType', value)}
            >
              <SelectTrigger className="w-full sm:w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FULFILLMENT_TYPE_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sort Order dropdown with label */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-muted-foreground">{t('orders.filter.sort')}</span>
            <Select
              value={filters.sortOrder}
              onValueChange={(value: SortOrderFilter) => updateFilter('sortOrder', value)}
            >
              <SelectTrigger className="w-full sm:w-[130px]">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_ORDER_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Vertical divider */}
          <div className="h-10 w-px bg-border self-end" />

          {/* Popover filters section with centered label */}
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground text-center">{t('orders.filter.filters')}</span>
            <div className="flex items-center gap-2">
              {/* Color filter + match mode */}
              <div className={cn(
                'flex items-center gap-1.5 rounded-md transition-all duration-200',
                filters.colorMatchMode === 'only' && filters.colors.length >= 1
                  ? 'bg-primary/5 ring-1 ring-primary/20 px-1.5 py-0.5'
                  : ''
              )}>
                <PopoverFilter
                  label={t('orders.filter.color')}
                  icon={Palette}
                  options={availableColors}
                  selected={filters.colors}
                  onChange={(selected) => {
                    const updates: Partial<OrdersPageFilters> = { colors: selected };
                    if (selected.length === 0) updates.colorMatchMode = 'any';
                    onChange({ ...filters, ...updates });
                  }}
                />
                <AnimatePresence>
                  {filters.colors.length >= 1 && (
                    <motion.div
                      {...badgeAnimProps}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Tabs
                              value={filters.colorMatchMode}
                              onValueChange={(v) => updateFilter('colorMatchMode', v as 'any' | 'only')}
                            >
                              <TabsList className="h-7 p-0.5 gap-0" aria-label={t('orders.filter.colorMatchMode')}>
                                <TabsTrigger value="any" className="h-6 px-2 text-[11px] rounded-sm">
                                  {t('orders.filter.hasAny')}
                                </TabsTrigger>
                                <TabsTrigger value="only" className="h-6 px-2 text-[11px] rounded-sm">
                                  {t('orders.filter.hasOnly')}
                                </TabsTrigger>
                              </TabsList>
                            </Tabs>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                          {filters.colorMatchMode === 'any'
                            ? t('orders.filter.hasAnyColorTooltip')
                            : t('orders.filter.hasOnlyColorTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Size filter + match mode */}
              <div className={cn(
                'flex items-center gap-1.5 rounded-md transition-all duration-200',
                filters.sizeMatchMode === 'only' && filters.sizes.length >= 1
                  ? 'bg-primary/5 ring-1 ring-primary/20 px-1.5 py-0.5'
                  : ''
              )}>
                <PopoverFilter
                  label={t('orders.filter.size')}
                  icon={Ruler}
                  options={availableSizes}
                  selected={filters.sizes}
                  onChange={(selected) => {
                    const updates: Partial<OrdersPageFilters> = { sizes: selected };
                    if (selected.length === 0) updates.sizeMatchMode = 'any';
                    onChange({ ...filters, ...updates });
                  }}
                />
                <AnimatePresence>
                  {filters.sizes.length >= 1 && (
                    <motion.div
                      {...badgeAnimProps}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>
                            <Tabs
                              value={filters.sizeMatchMode}
                              onValueChange={(v) => updateFilter('sizeMatchMode', v as 'any' | 'only')}
                            >
                              <TabsList className="h-7 p-0.5 gap-0" aria-label={t('orders.filter.sizeMatchMode')}>
                                <TabsTrigger value="any" className="h-6 px-2 text-[11px] rounded-sm">
                                  {t('orders.filter.hasAny')}
                                </TabsTrigger>
                                <TabsTrigger value="only" className="h-6 px-2 text-[11px] rounded-sm">
                                  {t('orders.filter.hasOnly')}
                                </TabsTrigger>
                              </TabsList>
                            </Tabs>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-[200px] text-xs">
                          {filters.sizeMatchMode === 'any'
                            ? t('orders.filter.hasAnySizeTooltip')
                            : t('orders.filter.hasOnlySizeTooltip')}
                        </TooltipContent>
                      </Tooltip>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Product Type filter */}
              <PopoverFilter
                label={t('orders.filter.product')}
                icon={Shirt}
                options={availableProductTypes}
                selected={filters.productTypes}
                onChange={(selected) => updateFilter('productTypes', selected)}
              />

              {/* Tags filter */}
              <PopoverFilter
                label={t('orders.filter.tagsLabel')}
                icon={Tag}
                options={availableTags}
                selected={filters.tags}
                onChange={(selected) => updateFilter('tags', selected)}
              />
            </div>
          </div>
        </div>

        {/* Row 3: Active filter badges (only shown if filters active) */}
        <AnimatePresence mode="popLayout">
          {activeFilterCount > 0 && (
            <motion.div
              {...collapseAnimProps}
              initial="initial"
              animate="animate"
              exit="exit"
              className="flex flex-wrap items-center gap-2 pt-1 border-t"
            >
              {/* Color mode indicator + badges */}
              {filters.colors.length > 0 && filters.colorMatchMode === 'only' && (
                <motion.div key="color-only-label" {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="outline" className="text-[10px] border-primary/30 text-primary font-medium">
                    {t('orders.filter.hasOnly')}
                  </Badge>
                </motion.div>
              )}
              {filters.colors.map(color => (
                <motion.div key={`color-${color}`} {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="secondary" className="gap-1 pr-1">
                    {sanitizeFilterValue(color)}
                    <button
                      onClick={() => removeFilter('colors', color)}
                      aria-label={t('orders.filter.removeFilter', { value: color, type: 'color' })}
                      className="ml-1 p-1 rounded-full hover:bg-muted"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </motion.div>
              ))}

              {/* Size mode indicator + badges */}
              {filters.sizes.length > 0 && filters.sizeMatchMode === 'only' && (
                <motion.div key="size-only-label" {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="outline" className="text-[10px] border-primary/30 text-primary font-medium">
                    {t('orders.filter.hasOnly')}
                  </Badge>
                </motion.div>
              )}
              {filters.sizes.map(size => (
                <motion.div key={`size-${size}`} {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="secondary" className="gap-1 pr-1">
                    {sanitizeFilterValue(size)}
                    <button
                      onClick={() => removeFilter('sizes', size)}
                      aria-label={t('orders.filter.removeFilter', { value: size, type: 'size' })}
                      className="ml-1 p-1 rounded-full hover:bg-muted"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </motion.div>
              ))}

              {/* Product type badges */}
              {filters.productTypes.map(type => (
                <motion.div key={`type-${type}`} {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="secondary" className="gap-1 pr-1">
                    {sanitizeFilterValue(type)}
                    <button
                      onClick={() => removeFilter('productTypes', type)}
                      aria-label={`Remove ${type} product type filter`}
                      className="ml-1 p-1 rounded-full hover:bg-muted"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </motion.div>
              ))}

              {/* Tag badges */}
              {filters.tags.map(tag => (
                <motion.div key={`tag-${tag}`} {...badgeAnimProps} initial="initial" animate="animate" exit="exit">
                  <Badge variant="secondary" className="gap-1 pr-1">
                    {sanitizeFilterValue(tag)}
                    <button
                      onClick={() => removeFilter('tags', tag)}
                      aria-label={`Remove ${tag} tag filter`}
                      className="ml-1 p-1 rounded-full hover:bg-muted"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                </motion.div>
              ))}

              {/* Clear all button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-6 text-xs ml-auto"
              >
                {t('orders.filter.clearAll')}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </CardContent>
    </Card>
  );
}
